import React, { useState, useEffect } from 'react';
import config from '../../utils/config';
import ReactMarkdown from 'react-markdown';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import MarkdownRenderer from './MarkdownRenderer';
import './analyzer.css';

const TitlePdfAnalyzerAgent = ({ fileUrl, fileName }) => {
  // Get API key and App ID from environment variables
  const API_KEY = import.meta.env.VITE_WORDWARE_API_KEY;
  const appId = "5edfa954-c01e-4bf3-b1c8-0a20f483856a";
  
  // Local state for the analysis result, error messages, and loading status.
  const [analysisResult, setAnalysisResult] = useState("");
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Only run if a fileUrl is provided.
    if (!fileUrl) return;
    
    const runAnalysis = async () => {
        setIsLoading(true);
        // console.log("Starting PDF analysis for file:", fileName, fileUrl);
        try {
          const response = await fetch(`${config.apiBaseUrl}/wordware-title-analyze-run`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              appId, // our appId from above
              payload: {
                inputs: {
                  Title_PDF: {
                    type: "file",
                    file_type: "application/pdf",
                    file_url: fileUrl,
                    file_name: fileName || "document.pdf",
                  },
                },
                version: "^1.2",
              },
            }),
          });
        // console.log("API response status:", response.status);

        if (!response.ok) {
          const errData = await response.json();
          console.error("Run failed", errData);
          throw new Error(`Run failed: ${response.status}`);
        }

        // Get the response stream reader.
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let accumulatedResult = "";

        console.log("Beginning to read the stream...");

        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            console.log("Stream reading done.");
            break;
          }
          // Decode the current chunk of data.
          const chunk = decoder.decode(value, { stream: true });
        //   console.log("Received chunk:", chunk);
          buffer += chunk;
          // Split the buffer by newline – each complete line represents one data chunk.
          const lines = buffer.split("\n");
          // The last line may be incomplete, so we save it back to buffer.
          buffer = lines.pop();
          // Process each complete line.
          // Process each complete line.
        for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed) continue;
            try {
            const content = JSON.parse(trimmed);
            const valueObj = content.value;
            // console.log("Parsed chunk content:", valueObj);
            if (valueObj.type === "generation") {
                if (valueObj.state === "start") {
                accumulatedResult += `\nNEW GENERATION - ${valueObj.label}\n`;
                } else {
                accumulatedResult += `\nEND GENERATION - ${valueObj.label}\n`;
                }
            } else if (valueObj.type === "chunk") {
                accumulatedResult += valueObj.value || "";
            } else if (valueObj.type === "outputs") {
                // Instead of concatenating, assign the final output.
                accumulatedResult = valueObj.values["home_title_generation"];
                // Optionally, you might break out of the loop if this is the final result:
                // break;
            }
            // console.log("Accumulated result so far:", accumulatedResult);
            // Update state with the latest accumulated result.
            setAnalysisResult(accumulatedResult);
            } catch (e) {
            console.error("Error parsing JSON chunk:", e, "Line:", trimmed);
            }
        }
        }  
        // Release the lock on the stream.
        reader.releaseLock();
      } catch (err) {
        console.error("Analysis error:", err);
        setError(err.message);
      } finally {
        setIsLoading(false);
        // console.log("PDF analysis completed. Final result:", analysisResult);
      }
    };

    runAnalysis();
  }, [fileUrl, fileName, API_KEY, appId]);

  function normalizeObjectionFences(text) {
    return text
      // open fence must be on its own line
      .replace(/```objection\s*/g, "\n\n```objection\n")
      // closing fence must also be on its own line
      .replace(/\s*```(\s|$)/g, "\n```\n\n");
  }

  return (
    <div>
      {isLoading && 
        <div className='flex flex-col gap-2 md:w-full items-center justify-center'>
          <LoadingSpinner />
          <p className='font-inter text-shi-green text-[16px] md:text-[24px]'>Analyzing Document</p>
          <p className='font-inter text-slate-600 text-[14px] md:text-[20px]'>This usually take between 10 to 20 seconds to complete</p>
        </div>}
      {error && <p style={{ color: 'red' }}>Error: {error}</p>}
      {analysisResult && (
        <div>
          <h3 className='text-[24px] mb-4'>Analysis Result</h3>
          <div className='markdown-content'>
            <MarkdownRenderer content={normalizeObjectionFences(analysisResult)} />
          </div>
        </div>
      )}
    </div>
  );
};

export default TitlePdfAnalyzerAgent;
